/**
 * An alert rule as the Alerts centre builds it and every surface reads it
 * back (brief §5.5): a few choices per step, written as one sentence. Shared
 * so the dashboard's rule list, the builder's live summary and an email's
 * "Why you got this" say the same words.
 */

/** The feed's regions (src/shared/feed/model.ts), less "Other". */
export const RULE_REGIONS = [
  'US',
  'UK',
  'EU',
  'AU/NZ',
  'CA',
  'Asia',
  'LatAm',
] as const;
export type RuleRegion = (typeof RULE_REGIONS)[number];

/**
 * The ten broad sectors the builder offered before it offered the sectors
 * raises are tagged with. Sources tag raises in their own words ("Fintech &
 * Finance", "Salud digital"), so the server files each raise under these by
 * its tags (sectorBuckets in src/alerts/alert.rules.ts). Rules saved then
 * still hold them, and the Free Alerts page's example rule asks one.
 */
export const BROAD_SECTORS = [
  'SaaS',
  'Fintech',
  'Healthtech',
  'Medtech',
  'Robotics',
  'AgTech',
  'Energy',
  'Food & drink',
  'Consumer',
  'Sports & leisure',
] as const;
export type BroadSector = (typeof BROAD_SECTORS)[number];

/** How many sectors the builder offers: the most common among live raises. */
export const SECTOR_CHOICES = 16;

/** The "Minimum ticket" steps, in the rule's currency. */
export const TICKET_STEPS = [100, 250, 500, 1000] as const;

export const TICKET_CURRENCIES = ['USD', 'GBP', 'EUR'] as const;
export type TicketCurrency = (typeof TICKET_CURRENCIES)[number];

const SYMBOL: Record<TicketCurrency, string> = { USD: '$', GBP: '£', EUR: '€' };

/**
 * When each alert fires. The three closing reminders are the deadline
 * sweep's CLOSING_SOON lead times; the rest are events the poll records.
 * PACE_UP is Momentum's Speeding up, the one pace alarm a rule offers.
 */
export const LEAD_TIMES = [
  'INSTANT',
  '7D',
  '72H',
  '24H',
  'NEAR_CAP',
  'PERK_EXPIRY',
  'TERM_CHANGE',
  'PACE_UP',
] as const;
export type LeadTime = (typeof LEAD_TIMES)[number];

/** As the builder's chips read. */
export const LEAD_LABELS: Record<LeadTime, string> = {
  INSTANT: 'Instant',
  '7D': '7d',
  '72H': '72h',
  '24H': '24h',
  NEAR_CAP: 'Near cap',
  PERK_EXPIRY: 'Perk expiry',
  TERM_CHANGE: 'Term change',
  PACE_UP: 'Speeding up',
};

/** The closing reminders, with the hours before the close each fires at. */
export const CLOSING_LEADS: Partial<Record<LeadTime, number>> = {
  '7D': 168,
  '72H': 72,
  '24H': 24,
};

/**
 * The moment a closing reminder is due: `hours` before the close, counted in
 * absolute hours, so a change of clocks moves the local time it shows at,
 * not when it goes. The deadline sweep records the crossing from this
 * (detectClosingSoon in src/ingestion/diff.ts), and the next closing-soon
 * run sends it; the deal page's preview names the same moment.
 */
export function closingReminderAt(deadlineAt: Date, hours: number): Date {
  return new Date(deadlineAt.getTime() - hours * 3_600_000);
}

export const CHANNELS = ['EMAIL', 'TELEGRAM', 'PUSH', 'RSS'] as const;
export type Channel = (typeof CHANNELS)[number];

export const CHANNEL_LABELS: Record<Channel, string> = {
  EMAIL: 'Email',
  TELEGRAM: 'Telegram',
  PUSH: 'Push',
  RSS: 'RSS',
};

/**
 * The channels that deliver today, and the only ones the dashboard offers.
 * The API refuses the others.
 */
export const LIVE_CHANNELS: readonly Channel[] = ['EMAIL'];

export interface QuietHours {
  /** Hour of the day, 0–23, in the rule's time zone. */
  from: number;
  until: number;
}

/** The builder's quiet-hours options; null is "Off". */
export const QUIET_OPTIONS: (QuietHours | null)[] = [
  null,
  { from: 22, until: 7 },
  { from: 23, until: 8 },
];

/** The builder's time zones. The viewer's own is offered too when not here. */
export const TIME_ZONES = [
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'Australia/Sydney',
] as const;

/** Everything a rule says. What the API takes and answers with. */
export interface RuleSpec {
  /** Empty: every region. */
  regions: RuleRegion[];
  /**
   * Empty: every sector. Otherwise sectors as raises are tagged with them,
   * spelled as the feed's Sector filter shows them, in the order chosen.
   */
  sectors: string[];
  /** The highest minimum ticket to hear about, in `ticketCurrency`; null is any. */
  maxTicket: number | null;
  ticketCurrency: TicketCurrency;
  /** Only raises open to investors in the subscriber's country of residence. */
  openToMe: boolean;
  leadTimes: LeadTime[];
  channels: Channel[];
  quietHours: QuietHours | null;
  timeZone: string;
}

/** Each list in the builder's own order, so a sentence never reshuffles. */
function ordered<T extends string>(values: T[], order: readonly T[]): T[] {
  return order.filter((v) => values.includes(v));
}

/** "€500", "$1,000". */
export function ticketAmount(amount: number, currency: TicketCurrency): string {
  return `${SYMBOL[currency]}${amount.toLocaleString('en-GB')}`;
}

/** "≤ €500", "≤ $1,000". */
export function ticketLabel(amount: number, currency: TicketCurrency): string {
  return `≤ ${ticketAmount(amount, currency)}`;
}

/** "22:00–07:00". */
export function quietLabel(quiet: QuietHours): string {
  const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
  return `${hh(quiet.from)}–${hh(quiet.until)}`;
}

export function sameQuiet(a: QuietHours | null, b: QuietHours | null) {
  return a === b || (!!a && !!b && a.from === b.from && a.until === b.until);
}

/**
 * The rule as its tokens, in the builder's order: “UK + EU”, “SaaS,
 * Fintech”, “min ticket ≤ €500”, “open to me”, “instant + 72h + near cap”,
 * “Email + Telegram”. Leave out parts with `only`, as an email's "Why you
 * got this" does with the channels.
 */
export function ruleTokens(
  spec: RuleSpec,
  only: { filters?: boolean; leads?: boolean; channels?: boolean } = {},
): string[] {
  const { filters = true, leads = true, channels = true } = only;
  const out: string[] = [];
  if (filters) {
    const regions = ordered(spec.regions, RULE_REGIONS);
    out.push(regions.length ? regions.join(' + ') : 'All regions');
    out.push(spec.sectors.length ? spec.sectors.join(', ') : 'All sectors');
    if (spec.maxTicket !== null) {
      out.push(
        `min ticket ${ticketLabel(spec.maxTicket, spec.ticketCurrency)}`,
      );
    }
    if (spec.openToMe) out.push('open to me');
  }
  if (leads && spec.leadTimes.length) {
    out.push(
      ordered(spec.leadTimes, LEAD_TIMES)
        .map((l) => LEAD_LABELS[l].toLowerCase())
        .join(' + '),
    );
  }
  if (channels && spec.channels.length) {
    out.push(
      ordered(spec.channels, CHANNELS)
        .map((c) => CHANNEL_LABELS[c])
        .join(' + '),
    );
  }
  return out;
}

/**
 * The rule's name as an email's "Why you got this" starts it, "UK + EU ·
 * Energy, SaaS, Fintech": its regions and sectors. The other filters go on
 * the line under it, the lead times are the rows. Rules have no name of
 * their own, so this is the one they go by.
 */
export function ruleName(spec: RuleSpec): string {
  return ruleTokens(spec, { leads: false, channels: false })
    .slice(0, 2)
    .join(' · ');
}

/** “UK + EU · SaaS, Fintech · instant + 72h”, quoted, for a line of prose. */
export function ruleQuote(
  spec: RuleSpec,
  only?: Parameters<typeof ruleTokens>[1],
): string {
  return `“${ruleTokens(spec, only).join(' · ')}”`;
}

/** "Quiet 22:00–07:00 · Europe/London", or "No quiet hours · …". */
export function quietLine(spec: Pick<RuleSpec, 'quietHours' | 'timeZone'>) {
  return [
    spec.quietHours ? `Quiet ${quietLabel(spec.quietHours)}` : 'No quiet hours',
    spec.timeZone,
  ].join(' · ');
}

/** The closing reminders a rule asks for, in prose: "7-day and 72-hour". */
export function reminderPhrase(leads: LeadTime[]): string | null {
  const words = ordered(leads, LEAD_TIMES)
    .filter((l) => CLOSING_LEADS[l])
    .map((l) => (l === '7D' ? '7-day' : `${CLOSING_LEADS[l]}-hour`));
  if (words.length === 0) return null;
  return words.length === 1
    ? words[0]
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * A rule's alert as the delivery history and the preview name it, from the
 * event that fired it: "New raise", "72 h to close", "Near cap". `long`
 * spells the hours out, as the preview does ("72 hours to close").
 */
export function alertLabel(
  type: string,
  dedupeKey: string,
  long = false,
): string {
  switch (type) {
    case 'NEW':
      return 'New raise';
    case 'CLOSING_SOON':
      if (dedupeKey === '168') return '7 days to close';
      return long ? `${dedupeKey} hours to close` : `${dedupeKey} h to close`;
    case 'NEAR_CAP':
      return 'Near cap';
    case 'PERK_ENDING':
      return 'Perk expiring';
    case 'TERMS_CHANGED':
      return 'Terms updated';
    case 'EXTENDED':
      return 'Deadline extended';
    case 'DEADLINE_CHANGED':
      return 'Deadline moved';
    case 'PACE_UP':
      return 'Speeding up';
    case 'PACE_SIGHT':
      return 'Goal in sight';
    case 'PACE_DOWN':
      return 'Slowing down';
    default:
      return type;
  }
}

/**
 * How an alert's kind is toned: the urgency scale, near cap, the accent, or
 * neutral for Slowing down, which never takes an urgency colour.
 */
export function alertTone(
  type: string,
  dedupeKey: string,
): 'critical' | 'urgent' | 'cap' | 'accent' | 'neutral' {
  if (type === 'CLOSING_SOON')
    return dedupeKey === '168' ? 'urgent' : 'critical';
  if (type === 'PERK_ENDING') return 'critical';
  if (type === 'NEAR_CAP') return 'cap';
  if (type === 'PACE_DOWN') return 'neutral';
  return 'accent';
}
